import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Chip, CircularProgress, MenuItem, Pagination, Stack, TextField, Typography } from "@mui/material";
import AffiliateNotice from "../../components/AffiliateNotice";
import { AffiliateAccount, TierCode, TierConfiguration } from "../../util/api/affiliateApi";
import { api, errorMessage } from "./api";
import AffiliateDiscountCodes from "./AffiliateDiscountCodes";

export default function AffiliatesPanel({ csrf, configuration, canManageDiscounts = false }: { csrf: string; configuration: TierConfiguration | null; canManageDiscounts?: boolean }) {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [state, setState] = useState("all");
  const [verified, setVerified] = useState("all");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<AffiliateAccount[]>([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<AffiliateAccount | null>(null);
  const [tier, setTier] = useState<TierCode | "automatic">("automatic");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const submitting = useRef(false);
  const generation = useRef(0);

  const load = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true); setError("");
    try {
      const result = await api.queryAffiliates({ search: query, approval_state: state === "all" ? null : state, verified: verified === "all" ? null : verified === "yes", page, page_size: 25 }, csrf);
      if (current === generation.current) { setRows(result.items); setTotal(result.total); }
    } catch (reason) { if (current === generation.current) setError(errorMessage(reason)); }
    finally { if (current === generation.current) setLoading(false); }
  }, [query, state, verified, page, csrf]);
  const invalidateRequests = useCallback(() => { generation.current += 1; }, []);
  useEffect(() => { load(); return invalidateRequests; }, [load, invalidateRequests]);

  const run = async (action: () => Promise<void>) => {
    if (submitting.current) return;
    submitting.current = true; setBusy(true); setError(""); setMessage("");
    try { await action(); } catch (reason) { setError(errorMessage(reason)); }
    finally { submitting.current = false; setBusy(false); }
  };
  const select = (account: AffiliateAccount) => { setSelected(account); setTier(account.assigned_tier?.code || "automatic"); };
  const changeState = (approvalState: "active" | "suspended") => run(async () => {
    if (!selected) return;
    select(await api.setAffiliateStatus(selected.id, approvalState, csrf));
    setMessage(`Account ${approvalState === "active" ? "activated" : "suspended"}.`); await load();
  });
  const date = (value: string | null) => value ? `${value.replace("T", " ")} UTC` : "—";

  return <Stack spacing={3} sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
    <Typography component="h2" variant="h5">Accounts</Typography>
    {error && <AffiliateNotice severity="error">{error}<Button onClick={load} disabled={loading}>Retry listing</Button></AffiliateNotice>}
    {message && <AffiliateNotice severity="success" role="status">{message}</AffiliateNotice>}
    <Box component="form" onSubmit={(event: FormEvent) => { event.preventDefault(); setPage(1); setQuery(search.trim()); }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: "minmax(0, 1fr) 160px 170px auto" }, gap: 2, alignItems: "start" }}>
        <TextField label="Search email or name" value={search} onChange={event => setSearch(event.target.value)} fullWidth />
        <TextField select label="Approval state" value={state} onChange={event => { setState(event.target.value); setPage(1); }} sx={{ minWidth: 160 }}>
          <MenuItem value="all">All states</MenuItem><MenuItem value="pending">Pending</MenuItem><MenuItem value="active">Active</MenuItem><MenuItem value="suspended">Suspended</MenuItem>
        </TextField>
        <TextField select label="Email verification" value={verified} onChange={event => { setVerified(event.target.value); setPage(1); }} sx={{ minWidth: 170 }}>
          <MenuItem value="all">All emails</MenuItem><MenuItem value="yes">Verified</MenuItem><MenuItem value="no">Unverified</MenuItem>
        </TextField>
        <Button type="submit" disabled={loading}>Search</Button>
      </Box>
    </Box>
    {loading && <CircularProgress aria-label="Loading accounts" />}
    {!loading && rows.length === 0 && <Typography>No accounts match these filters.</Typography>}
    {rows.map(account => <Box key={account.id} sx={{ borderBottom: 1, borderColor: "divider", pb: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
        <Box sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}><Typography>{account.display_name}</Typography><Typography color="text.secondary">{account.email}</Typography></Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", minWidth: 0 }}>
          <Chip label={account.approval_state} /><Chip label={account.email_verified_at ? "Verified" : "Unverified"} />
          <Button disabled={busy} sx={{ overflowWrap: "anywhere", minWidth: 0 }} onClick={() => run(async () => select(await api.affiliateDetail(account.id, csrf)))}>Review {account.display_name}</Button>
        </Box>
      </Stack>
    </Box>)}
    {total > 25 && <Pagination siblingCount={0} count={Math.ceil(total / 25)} page={page} onChange={(_, value) => setPage(value)} disabled={loading} />}
    {selected && <Box sx={{ p: { xs: 2, sm: 3 }, border: 1, borderColor: "divider", borderRadius: 2 }}><Stack spacing={2}>
      <Typography component="h3" variant="h6" sx={{ overflowWrap: "anywhere" }}>Review: {selected.display_name}</Typography>
      <Box component="dl" sx={{ m: 0, "& dt": { mt: 1, color: "text.secondary" }, "& dd": { m: 0, overflowWrap: "anywhere" } }}>
        <dt>Email</dt><dd>{selected.email}</dd><dt>Status</dt><dd>{selected.approval_state}</dd>
        <dt>Email verified</dt><dd>{date(selected.email_verified_at)}</dd>
        <dt>Assigned percentage</dt><dd>{Number(selected.commission_percentage).toFixed(2)}%</dd>
        <dt>Paid sales</dt><dd>{selected.sales_count ?? 0}</dd>
        <dt>Current rank</dt><dd>{selected.tier?.code || "Not configured"} · {selected.tier_assignment || "automatic"}</dd>
        <dt>Earned rank</dt><dd>{selected.earned_tier?.code || "Not configured"}</dd>
        <dt>Starting rank</dt><dd>{selected.assigned_tier?.code || "No minimum"}</dd>
        <dt>Referral code</dt><dd>{selected.referral_code || "Not generated"}</dd>
        <dt>Created</dt><dd>{date(selected.created_at)}</dd><dt>Updated</dt><dd>{date(selected.updated_at)}</dd>
        <dt>Approved</dt><dd>{date(selected.approved_at)}</dd><dt>Approving administrator ID</dt><dd>{selected.approved_by_id || "—"}</dd>
        <dt>Last login</dt><dd>{date(selected.last_login_at)}</dd>
      </Box>
      <AffiliateDiscountCodes key={selected.id} affiliateId={selected.id} csrf={csrf} editable={canManageDiscounts}
        eligible={selected.approval_state === "active" && !!selected.email_verified_at} />
      {!selected.email_verified_at && <AffiliateNotice severity="info">Email confirmation is required before activation.</AffiliateNotice>}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <Button variant="contained" disabled={busy || !selected.email_verified_at || selected.approval_state === "active"} onClick={() => changeState("active")}>Activate account</Button>
        <Button color="error" disabled={busy || selected.approval_state === "suspended"} onClick={() => changeState("suspended")}>Suspend account</Button>
      </Stack>
      {configuration?.enabled ? <Box component="form" onSubmit={(event: FormEvent) => {
        event.preventDefault();
        run(async () => { select(await api.assignTier(selected.id, tier === "automatic" ? null : tier, csrf)); setMessage("Rank assignment saved."); await load(); });
      }}><Stack spacing={2}>
        <TextField select label="Starting rank" helperText="Sales can unlock higher ranks automatically." value={tier} onChange={event => setTier(event.target.value as TierCode | "automatic")}>
          <MenuItem value="automatic">No minimum — sales-based progression</MenuItem>
          {configuration.tiers.map(value => <MenuItem key={value.code} value={value.code} sx={{ textTransform: "capitalize" }}>{value.code} · {value.commission_percentage}%</MenuItem>)}
        </TextField>
        <Button type="submit" disabled={busy}>Save rank</Button>
      </Stack></Box> : <Typography color="text.secondary">Configure the tier ladder before assigning ranks.</Typography>}
    </Stack></Box>}
  </Stack>;
}
